import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone, Send, Globe, Clock, ArrowUpRight, Linkedin, Twitter, MessageCircle, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { COMPANY, OFFICES } from "@/data/site";
import { GlowOrb } from "@/components/common/GlowOrb";
import Seo from "@/components/seo/Seo";
import { trackClick, trackEvent } from "@/lib/analytics";

/**
 * Contact page — three-office presence (Karachi, Islamabad, Glasgow) with
 * a big cinematic hero, quick-contact chips, per-office cards, contact form,
 * and a social/website strip.
 */
export default function Contact() {
  const { search } = useLocation();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [intent, setIntent] = useState(null); // "15-min-consultation" | "discuss-your-project" | null

  // Phase 3.3: read query params so CTAs from service/case-study pages can
  // prefill Contact with meaningful subject + message context.
  useEffect(() => {
    const p = new URLSearchParams(search);
    const subject = p.get("subject");
    if (!subject) return;
    const serviceName = p.get("service_name") || p.get("service");
    const caseName = p.get("case_study_name") || p.get("case_study");
    const market = p.get("market");
    setIntent(subject);
    if (subject === "15-min-consultation") {
      const contextBits = [];
      if (serviceName) contextBits.push(`Service: ${serviceName}`);
      if (caseName) contextBits.push(`Similar to: ${caseName}`);
      if (market) contextBits.push(`Market: ${market}`);
      setForm((s) => ({
        ...s,
        subject: `Book a 15-minute consultation${serviceName ? ` — ${serviceName}` : ""}`,
        message:
          s.message ||
          `Hi CloudLab team,\n\nI'd like to book a 15-minute consultation to discuss my project.\n\n${contextBits.join("\n")}\n\nBest times to reach me:`,
      }));
      trackEvent("consultation_page_view", {
        source_page: p.get("source_page") || undefined,
        service_slug: p.get("service") || undefined,
        case_study_slug: p.get("case_study") || undefined,
        market_slug: p.get("market") || undefined,
      });
    } else if (subject === "discuss-your-project") {
      setForm((s) => ({
        ...s,
        subject: "Discuss a project",
        message:
          s.message ||
          `Hi CloudLab team,\n\nI'd like to discuss a potential project.\n\n${serviceName ? `Interested in: ${serviceName}\n` : ""}`,
      }));
    }
  }, [search]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Preserve intent for analytics + backend context
      const payload = intent ? { ...form, intent } : form;
      await api.post("/contact", payload);
      toast.success(
        intent === "15-min-consultation"
          ? "Consultation request sent. We'll reply with a time within 24 hours."
          : "Message sent. We'll reply within 24 hours.",
      );
      trackEvent("contact_submitted", { intent: intent || "general" });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact"
        description="Get in touch with CloudLab Technologies — offices in Karachi, Islamabad and Glasgow. Serving clients across the UK, USA, Germany, KSA, UAE and Pakistan."
        keywords="contact CloudLab, CloudLab Karachi office, CloudLab Islamabad office, CloudLab Glasgow UK, hire CloudLab, get a quote"
      />

      {/* ---------- Hero ---------- */}
      <section className="relative overflow-hidden pt-24 pb-16 md:pt-32 md:pb-24">
        <GlowOrb className="-top-40 -right-40" color="primary" size={640} />
        <GlowOrb className="bottom-0 -left-40" color="secondary" size={420} />
        <div className="relative max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-primary">
              <Sparkles className="w-3.5 h-3.5" /> Contact CloudLab
            </div>
            <h1 className="mt-5 font-heading font-black tracking-tighter text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
              Let&apos;s <span className="text-gradient-aurora italic">build</span> something serious together.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Three offices. Two continents. One senior team that ships. Reach us in the way that suits you — a call, a WhatsApp, an email, or drop into any of our studios.
            </p>
            {intent === "15-min-consultation" && (
              <div className="mt-6 inline-flex items-center gap-2 rounded-2xl border border-primary/30 bg-primary/[0.06] px-4 py-2.5 text-sm" data-testid="contact-consultation-banner">
                <Clock className="w-4 h-4 text-primary" />
                <span className="text-muted-foreground">Booking a</span>
                <span className="font-medium text-foreground">15-minute consultation</span>
                <span className="text-muted-foreground">— send us a note and we&apos;ll come back with a time.</span>
              </div>
            )}
          </motion.div>

          {/* Quick contact chips */}
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <QuickChip icon={Mail} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} testid="contact-quick-email" onClick={() => trackClick("email_clicked", { email: COMPANY.email, surface: "contact_quick" })} />
            <QuickChip icon={Phone} label="Pakistan" value="+92 345 5222 000" href="tel:+923455222000" testid="contact-quick-pk" onClick={() => trackClick("phone_clicked", { number: "+92 345 5222 000", surface: "contact_quick", market: "pk" })} />
            <QuickChip icon={Phone} label="United Kingdom" value="+44 73 8088 5506" href="tel:+447380885506" testid="contact-quick-uk" onClick={() => trackClick("phone_clicked", { number: "+44 73 8088 5506", surface: "contact_quick", market: "uk" })} />
            <QuickChip icon={Globe} label="Website" value="cloudlabweb.com" href={COMPANY.website} external testid="contact-quick-web" />
          </div>
        </div>
      </section>

      {/* ---------- Offices grid ---------- */}
      <section className="relative py-16 md:py-24 border-t border-border">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between gap-4 mb-10">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-primary mb-3">Our offices</div>
              <h2 className="font-heading font-black tracking-tight text-3xl md:text-4xl">
                Come say hello.
              </h2>
            </div>
            <div className="hidden md:block text-xs text-muted-foreground">
              Open <span className="text-foreground font-medium">Mon – Fri</span> · <Clock className="w-3 h-3 inline -mt-0.5" /> 09:00 – 19:00 local
            </div>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            {OFFICES.map((o, i) => (
              <OfficeCard key={o.id} office={o} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Form + Socials ---------- */}
      <section className="relative py-16 md:py-24 border-t border-border">
        <GlowOrb className="top-1/2 -left-40 -translate-y-1/2" color="primary" size={420} />
        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[1fr_1.1fr] gap-12 items-start">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-primary mb-3">Send a message</div>
            <h2 className="font-heading font-black tracking-tight text-3xl md:text-4xl leading-tight">
              Prefer to write?<br className="hidden md:block" /> We reply in 24 hours.
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed max-w-md">
              Every message lands directly in a senior engineer&apos;s inbox — no gatekeepers, no automated tickets.
            </p>

            {/* Socials */}
            <div className="mt-10">
              <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-4">
                Follow the work
              </div>
              <div className="flex flex-wrap gap-3">
                <SocialLink href={COMPANY.socials.linkedin} label="LinkedIn" testid="social-linkedin">
                  <Linkedin className="w-4 h-4" />
                </SocialLink>
                <SocialLink href={COMPANY.socials.twitter} label="X / Twitter" testid="social-x">
                  <Twitter className="w-4 h-4" />
                </SocialLink>
                <SocialLink href="https://wa.me/923455222000" label="WhatsApp" testid="social-whatsapp" onClick={() => trackClick("whatsapp_clicked", { number: "+923455222000", surface: "contact_page", market: "pk" })}>
                  <MessageCircle className="w-4 h-4" />
                </SocialLink>
              </div>
            </div>
          </div>

          <form onSubmit={submit} className="glass rounded-3xl p-6 md:p-8 space-y-4 shadow-[0_25px_70px_-25px_hsl(var(--primary)/0.35)]" data-testid="contact-form">
            <div className="grid md:grid-cols-2 gap-4">
              <FormField label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-name" />
              <FormField label="Email" required type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" />
            </div>
            <FormField label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testid="contact-subject" />
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Message</label>
              <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-2 w-full bg-transparent border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 resize-none"
                placeholder="Tell us what you're working on…"
                data-testid="contact-message-input" />
            </div>
            <button disabled={loading} type="submit"
              className="w-full rounded-full px-6 py-4 bg-primary text-primary-foreground text-sm font-semibold btn-neon inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="contact-submit-btn">
              <Send className="w-4 h-4" /> {loading ? "Sending…" : "Send message"}
            </button>
            <div className="text-[11px] text-center text-muted-foreground">
              {intent === "15-min-consultation"
                ? "We reply to consultation requests within 24 hours with a suggested time."
                : "Or open the AI Agent (bottom-right) for an instant reply."}
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

// ---------- Building blocks ----------
function QuickChip({ icon: Icon, label, value, href, external, testid, onClick }) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      onClick={onClick}
      data-testid={testid}
      className="group flex items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3 hover:border-primary/50 hover:bg-primary/[0.03] transition-colors"
    >
      <div className="shrink-0 w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
        <div className="font-medium text-sm truncate">{value}</div>
      </div>
    </a>
  );
}

function OfficeCard({ office: o, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="group relative rounded-3xl border border-border bg-card overflow-hidden flex flex-col"
      data-testid={`office-card-${o.id}`}
    >
      {/* aurora accent */}
      <div className="absolute inset-x-0 -top-24 h-40 opacity-70 group-hover:opacity-100 transition-opacity blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(50% 60% at 50% 40%, hsl(var(--primary) / 0.35), transparent 70%)" }}
      />
      <div className="relative p-6 md:p-7 flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2">
            <span className="text-2xl leading-none">{o.flag}</span>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">{o.country}</div>
              <div className="font-heading font-bold text-xl leading-tight">{o.city}</div>
            </div>
          </div>
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary bg-primary/10 rounded-full px-2.5 py-1 whitespace-nowrap">
            {o.label}
          </span>
        </div>

        <div className="mt-5 flex items-start gap-3">
          <MapPin className="w-4 h-4 text-primary shrink-0 mt-0.5" />
          <p className="text-sm leading-relaxed text-foreground/85">{o.address}</p>
        </div>

        <div className="mt-4 space-y-2 text-sm text-muted-foreground">
          <a href={`tel:${o.phone.replace(/\s+/g, "")}`} className="flex items-center gap-3 hover:text-primary transition-colors" data-testid={`office-phone-${o.id}`} onClick={() => trackClick("phone_clicked", { number: o.phone, surface: "contact_office", office: o.id })}>
            <Phone className="w-4 h-4 shrink-0" /> <span className="tracking-wide">{o.phone}</span>
          </a>
          <a href={`mailto:${o.email}`} className="flex items-center gap-3 hover:text-primary transition-colors" data-testid={`office-email-${o.id}`} onClick={() => trackClick("email_clicked", { email: o.email, surface: "contact_office", office: o.id })}>
            <Mail className="w-4 h-4 shrink-0" /> <span>{o.email}</span>
          </a>
          <div className="flex items-center gap-3">
            <Clock className="w-4 h-4 shrink-0" /> <span>{o.hours} · {o.tz}</span>
          </div>
        </div>

        <div className="mt-6 pt-5 border-t border-border">
          <a
            href={o.map}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:gap-3 transition-all"
            data-testid={`office-directions-${o.id}`}
          >
            Get directions <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </motion.div>
  );
}

function SocialLink({ href, label, testid, children, onClick }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:border-primary/60 hover:bg-primary/[0.04] transition-colors"
    >
      {children}
      <span>{label}</span>
    </a>
  );
}

function FormField({ label, value, onChange, required, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">{label}{required && " *"}</label>
      <input
        required={required} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-transparent border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
        data-testid={`${testid}-input`}
      />
    </div>
  );
}
